import { Diagnostic, logDiagnostic } from './logDiagnostic';

type RuntimeConfig = Record<string, string | undefined>;

/** Left behind when a container starts without the env var the index.html placeholder needs. */
const UNSUBSTITUTED_PLACEHOLDER = /^\$\{?UNSAFE_[A-Z0-9_]+\}?$/;

const fixHints = (envVar: string): string[] => [
  `Local dev: set ${envVar} in .env.local and restart "npm start".`,
  `Deployed env: set ${envVar} on the web container app, then redeploy so start.sh can render index.html with it.`,
];

/**
 * Validates the runtime config the page was served with. Runs before the app reads
 * `window.anovaAppConfig`, so a missing value is reported instead of surfacing later as an
 * undefined base URL or a request that silently goes to the wrong place.
 */
export function readRuntimeConfig(): RuntimeConfig | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return (window as unknown as { anovaAppConfig?: RuntimeConfig })
    .anovaAppConfig;
}

export function checkRuntimeConfig(
  config: RuntimeConfig | undefined = readRuntimeConfig(),
): Diagnostic[] {
  const problems: Diagnostic[] = [];
  const report = (diagnostic: Diagnostic): void => {
    problems.push(diagnostic);
    logDiagnostic(diagnostic);
  };

  if (!config) {
    report({
      key: 'runtime-config:missing',
      title: 'Runtime config window.anovaAppConfig is missing',
      details: {
        expected:
          'index.html defines window.anovaAppConfig with the API base URLs',
      },
      fix: [
        'Local dev: restart "npm start" — the anova-html-config Vite plugin injects the config into index.html.',
        'Deployed env: check the container logs for the "nginx.conf rendered" banner; start.sh renders index.html from index.html.template.',
        'Hard-reload the page (Ctrl+Shift+R) in case a cached index.html without the config is being served.',
      ],
    });
    return problems;
  }

  Object.entries(config).forEach(([key, raw]) => {
    const value = raw?.trim() ?? '';
    if (!UNSUBSTITUTED_PLACEHOLDER.test(value)) {
      return;
    }

    report({
      key: `runtime-config:placeholder:${key}`,
      title: `Runtime config ${key} still holds the ${value} placeholder`,
      details: {
        key: `window.anovaAppConfig.${key}`,
        value,
      },
      fix: fixHints(value.replace(/^\$\{?/, '').replace(/\}?$/, '')),
    });
  });

  return problems;
}
